// THE NETTI TUNER'S SHARED LAW — client-safe, used by the tuner page, its API
// routes and the dashboard mount alike, so "what may a dial be" is decided once.
//
// Three layers make a person's body, in this order:
//   1. the MINT      futureTunersFromSeed(seed, bands) — born from who they are
//   2. the OVERRIDE  a founder's hand-tuned patch over that mint (migration 111)
//   3. a surface's own dressing (`tune`) — never identity, not this file's concern
//
// COLOUR rides the same way (migration 112): the mint is futurePalette(identity)
// — a curated palette picked by the seed, spun by a per-seed hue rotation — and
// an override replaces it WHOLE, because a palette is a chord and half a chord
// is a different colour. Everything stored here is CURATED, never vivid: the
// engine applies its own vivid pass at render and the UI tint reads the same
// curated pins, so one stored palette dresses the creature and the dashboard
// alike (law_organism_color_authority).
//
// Nothing here touches age: age is memory (ageFromMemory) and cannot be set.

import type { ColorState, HueSL } from "../organism/color";
import {
  FUTURE_MINT_BANDS,
  FUTURE_TUNER_DEFAULTS,
  futureTunersFromSeed,
  type FutureMintBands,
  type FutureTuners,
} from "../organism/future/futureOrganism";

export type TunerKey = keyof FutureTuners;
export type TunerPatch = Partial<Record<TunerKey, number>>;

export const TUNER_KEYS = Object.keys(FUTURE_TUNER_DEFAULTS) as TunerKey[];

export type DialGroup = { id: string; label: string; hint: string; dials: { key: TunerKey; label: string; hint: string }[] };

/** The 24 dials, gathered the way a hand reaches for them. */
export const DIAL_GROUPS: DialGroup[] = [
  {
    id: "form",
    label: "form",
    hint: "how the body is arranged",
    dials: [
      { key: "formation", label: "formation", hint: "spiral, to rosette, to golden burst" },
      { key: "radialBias", label: "spread", hint: "tight centre, to an even ring" },
      { key: "abundance", label: "abundance", hint: "how many petals at full age" },
      { key: "variety", label: "variety", hint: "how unalike the petals are" },
    ],
  },
  {
    id: "petals",
    label: "petals",
    hint: "the shape of each one",
    dials: [
      { key: "petalLength", label: "length", hint: "" },
      { key: "petalWidth", label: "width", hint: "" },
      { key: "tipSharpness", label: "tip", hint: "round bulb, to pointed blade" },
      { key: "bend", label: "bend", hint: "the sickle along its length" },
    ],
  },
  {
    id: "matter",
    label: "matter",
    hint: "what it is made of",
    dials: [
      { key: "venation", label: "veins", hint: "" },
      { key: "nodes", label: "nodes", hint: "cells at the vein ends" },
      { key: "glass", label: "glass", hint: "how much light passes through" },
      { key: "innerLight", label: "inner light", hint: "" },
      { key: "hueSpread", label: "hue spread", hint: "how far the palette fans across petals" },
    ],
  },
  {
    id: "cell",
    label: "the cell",
    hint: "the heart at the centre",
    dials: [
      { key: "cellSize", label: "size", hint: "" },
      { key: "cellWobble", label: "wobble", hint: "" },
      { key: "cellGlow", label: "glow", hint: "a cell alone, as an orb of light" },
      { key: "cellLight", label: "light", hint: "how lit the core is, a dim pearl to full light. petals stay as they are" },
    ],
  },
  {
    id: "motion",
    label: "motion",
    hint: "behaviour, not identity",
    dials: [
      { key: "sway", label: "sway", hint: "" },
      { key: "tempo", label: "tempo", hint: "" },
      { key: "breath", label: "breath", hint: "" },
      { key: "revolve", label: "revolve", hint: "the dashboard owns rotation, so this shows here only" },
      { key: "motes", label: "motes", hint: "" },
      { key: "moteSize", label: "mote size", hint: "" },
      { key: "touch", label: "touch", hint: "how it answers the cursor" },
    ],
  },
];

const clamp = (v: number, lo: number, hi: number) => (v < lo ? lo : v > hi ? hi : v);
const round3 = (v: number) => Math.round(v * 1000) / 1000;
const wrapHue = (h: number) => ((h % 360) + 360) % 360;

// THE COLOUR RAILS. A palette may be any hue at all — hue is identity and the
// whole wheel is fair. What is held is the part that decides whether a netti
// reads as a living thing or as a smear: nothing so grey it stops being a
// colour, nothing so pale it burns out or so dark it closes up, and enough
// contrast for the centre to separate from the petals. These are the bands the
// curated mint itself has always drawn inside (sat .5–.68, light .47–.58,
// contrast .56–.78), opened a little so a hand has somewhere to go.
export const COLOR_RAILS = {
  sat: [0.35, 0.95] as [number, number],
  light: [0.4, 0.68] as [number, number],
  contrast: [0.5, 0.88] as [number, number],
};

// THE TWO RAILS no hand may cross, because they are the two combinations that
// expose the petal roots (see FUTURE_MINT_BANDS): the cell is never minted high
// and the spread never low. The tuner draws them on the track; the API enforces
// them, so a band that crosses one cannot be saved from anywhere.
export const BAND_RAILS: Partial<Record<TunerKey, { minLo?: number; maxHi?: number; why: string }>> = {
  cellSize: { maxHi: 0.4, why: "a big cell squeezes the petal roots into view" },
  radialBias: { minLo: 0.36, why: "a tight centre crams the roots together" },
};

const MIN_BAND_WIDTH = 0.04;

/** Known dials only, each clamped to 0..1. Anything else is dropped. */
export function sanitizeTuners(input: unknown): TunerPatch {
  const out: TunerPatch = {};
  if (!input || typeof input !== "object") return out;
  for (const k of TUNER_KEYS) {
    const v = (input as Record<string, unknown>)[k];
    if (typeof v === "number" && Number.isFinite(v)) out[k] = round3(clamp(v, 0, 1));
  }
  return out;
}

/** Known dials only; each band ordered, clamped, at least MIN_BAND_WIDTH wide,
 *  and held inside its rail. A dial that is absent is closed to the mint. */
export function sanitizeBands(input: unknown): FutureMintBands {
  const out: FutureMintBands = {};
  if (!input || typeof input !== "object") return out;
  for (const k of TUNER_KEYS) {
    const b = (input as Record<string, unknown>)[k];
    if (!Array.isArray(b) || b.length !== 2) continue;
    let lo = Number(b[0]);
    let hi = Number(b[1]);
    if (!Number.isFinite(lo) || !Number.isFinite(hi)) continue;
    if (lo > hi) [lo, hi] = [hi, lo];
    const rail = BAND_RAILS[k];
    lo = clamp(lo, rail?.minLo ?? 0, 1);
    hi = clamp(hi, 0, rail?.maxHi ?? 1);
    if (hi - lo < MIN_BAND_WIDTH) {
      hi = Math.min(rail?.maxHi ?? 1, lo + MIN_BAND_WIDTH);
      lo = Math.max(rail?.minLo ?? 0, hi - MIN_BAND_WIDTH);
    }
    out[k] = [round3(lo), round3(hi)];
  }
  return out;
}

function sanitizePin(input: unknown): HueSL | null {
  if (!input || typeof input !== "object") return null;
  const p = input as Record<string, unknown>;
  const hue = Number(p.hue);
  const sat = Number(p.sat);
  const light = Number(p.light);
  if (!Number.isFinite(hue) || !Number.isFinite(sat) || !Number.isFinite(light)) return null;
  return {
    hue: Math.round(wrapHue(hue) * 10) / 10,
    sat: round3(clamp(sat, COLOR_RAILS.sat[0], COLOR_RAILS.sat[1])),
    light: round3(clamp(light, COLOR_RAILS.light[0], COLOR_RAILS.light[1])),
  };
}

/** A whole curated palette, or null. A palette needs BOTH its pins: primary is
 *  the body, secondary the outer petals, and one without the other is not a
 *  palette. `tinge` is the optional third pin (derived when it is absent). */
export function sanitizeColor(input: unknown): ColorState | null {
  if (!input || typeof input !== "object") return null;
  const c = input as Record<string, unknown>;
  const primary = sanitizePin(c.primary);
  const secondary = sanitizePin(c.secondary);
  if (!primary || !secondary) return null;
  const tinge = sanitizePin(c.tinge);
  const contrast = Number(c.contrast);
  return {
    primary,
    secondary,
    ...(tinge ? { tinge } : {}),
    contrast: round3(clamp(Number.isFinite(contrast) ? contrast : 0.68, COLOR_RAILS.contrast[0], COLOR_RAILS.contrast[1])),
  };
}

const pinsEqual = (a: HueSL | undefined, b: HueSL | undefined) =>
  (!a && !b) || (!!a && !!b && Math.abs(a.hue - b.hue) < 0.05 && Math.abs(a.sat - b.sat) < 0.002 && Math.abs(a.light - b.light) < 0.002);

export function colorsEqual(a: ColorState | null, b: ColorState | null): boolean {
  if (!a || !b) return !a && !b;
  return (
    pinsEqual(a.primary, b.primary) &&
    pinsEqual(a.secondary, b.secondary) &&
    pinsEqual(a.tinge, b.tinge) &&
    Math.abs(a.contrast - b.contrast) < 0.002
  );
}

export function bandsEqual(a: FutureMintBands, b: FutureMintBands): boolean {
  for (const k of TUNER_KEYS) {
    const x = a[k];
    const y = b[k];
    if (!x !== !y) return false;
    if (x && y && (Math.abs(x[0] - y[0]) > 1e-6 || Math.abs(x[1] - y[1]) > 1e-6)) return false;
  }
  return true;
}

export const CODE_BANDS: FutureMintBands = FUTURE_MINT_BANDS;

/** The whole body a seed is born with under a band set: every dial, the mint
 *  where the mint is open and the product default where it is not. */
export function mintedBody(seed: number, bands: FutureMintBands | null): FutureTuners {
  return { ...FUTURE_TUNER_DEFAULTS, ...futureTunersFromSeed(seed, bands ?? CODE_BANDS) };
}

/** What a product mount lays over the code mint for this person, or null when
 *  there is nothing to lay: the live mint (only when the bands have moved off
 *  the code's) and then the hand-tuned override on top. `revolve` never rides:
 *  rotation belongs to the surface's choreography. */
export function resolveBody(seed: number, bands: FutureMintBands | null, override: TunerPatch | null): TunerPatch | null {
  const moved = bands && !bandsEqual(bands, CODE_BANDS);
  const o = override && Object.keys(override).length ? override : null;
  if (!moved && !o) return null;
  // a dial the live mint has CLOSED falls back to the product default — the
  // mount already wore the code's mint for it, so it has to be said out loud.
  const closed: TunerPatch = {};
  if (moved) for (const k of Object.keys(CODE_BANDS) as TunerKey[]) if (!bands[k]) closed[k] = FUTURE_TUNER_DEFAULTS[k];
  const body: TunerPatch = { ...closed, ...(moved ? futureTunersFromSeed(seed, bands) : {}), ...(o ?? {}) };
  delete body.revolve;
  return Object.keys(body).length ? body : null;
}
